import React, { useEffect, useState } from 'react';
import type { RenderFn } from '@atlassian/forge-ui-types';
import { DatePicker as PlatformDatePicker } from '@atlaskit/datetime-picker';
import { adaptEventHandler } from '../../utils';
import type { DateTimePickerSelectProps } from '@atlaskit/datetime-picker';
import type { FieldProps } from '@atlassian/forge-ui-types';
import { useIsInModal } from '../modal/ModalProvider';

type Appearance = 'default' | 'subtle' | 'none';
type Spacing = 'compact' | 'default';

/**
 * Type for date picker had to be defined here because the type could not be imported from the datepicker module.
 * It is being declared in typings/atlaskit.d.ts which is preventing imports. If removed, type errors are introduced in components
 * outside of this package.
 *
 */
export type DatePickerProps = {
	appearance?: Appearance;
	autoFocus?: boolean;
	dateFormat?: string;
	defaultIsOpen?: boolean;
	defaultValue?: string;
	disabled?: string[];
	isDisabled?: boolean;
	isInvalid?: boolean;
	isOpen?: boolean;
	locale?: string;
	maxDate?: string;
	minDate?: string;
	nextMonthLabel?: string;
	onChange?: (value: string) => void;
	placeholder?: string;
	previousMonthLabel?: string;
	selectProps?: DateTimePickerSelectProps;
	shouldShowCalendarButton?: boolean;
	spacing?: Spacing;
	testId?: string;
	value?: string;
	weekStartDay?: 0 | 1 | 2 | 3 | 4 | 5 | 6;
} & Partial<Omit<FieldProps, 'onChange'>>;

/**
 * A date picker allows the user to select a particular date.
 */
export const DatePicker = (props: Parameters<RenderFn>[0]): React.JSX.Element => {
	const {
		appearance,
		defaultIsOpen,
		autoFocus,
		defaultValue,
		disabled,
		maxDate,
		minDate,
		isOpen,
		nextMonthLabel,
		previousMonthLabel,
		selectProps,
		spacing,
		dateFormat,
		shouldShowCalendarButton,
		placeholder,
		locale,
		testId,
		weekStartDay,
		onChange,
		value,
		// Field props
		id,
		isRequired,
		isDisabled,
		isInvalid,
		onBlur,
		onFocus,
		'aria-invalid': ariaInvalid,
		'aria-labelledby': ariaLabelledby,
		name,
	} = props.forgeDoc.props as DatePickerProps;

	const { isInModal } = useIsInModal();

	// The below is to address the following bug from DST: https://product-fabric.atlassian.net/browse/DSP-15700
	// If the bug is fixed, the workaround below can be removed
	const [isInitialRenderDisabled, setIsInitialRenderDisabled] = useState(isInModal ?? false);

	useEffect(() => {
		if (isInitialRenderDisabled) {
			setTimeout(() => {
				setIsInitialRenderDisabled(false);
				// from testing, 800 ms was the minimum time required for the modal to mount first without impacting the datepicker calendar
			}, 800);
		}
		// disabled to ensure this only ever runs once on mount
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<PlatformDatePicker
			// Updates the id to force a remount of the component. This is required to ensure certain props like `autoFocus` provided by the developer is properly applied.
			// `autoFocus` is only applied on initial mount and if isDisabled is set, it does not activate the calendar even when isDisabled is returned to false.
			key={isInitialRenderDisabled ? '1' : '2'}
			isDisabled={isInitialRenderDisabled ? true : isDisabled}
			appearance={appearance}
			defaultIsOpen={defaultIsOpen}
			autoFocus={autoFocus}
			defaultValue={defaultValue}
			disabled={disabled}
			maxDate={maxDate}
			minDate={minDate}
			// undefined is evaluated as true when isOpen is being explicitly set
			{...(isOpen ? { isOpen } : undefined)}
			nextMonthLabel={nextMonthLabel}
			previousMonthLabel={previousMonthLabel}
			selectProps={selectProps}
			spacing={spacing}
			dateFormat={dateFormat}
			shouldShowCalendarButton={shouldShowCalendarButton}
			placeholder={placeholder}
			locale={locale}
			testId={testId}
			weekStartDay={weekStartDay}
			onChange={onChange}
			// Explicitly setting value to undefined is causing issues
			{...(value !== undefined ? { value } : undefined)}
			id={id}
			isRequired={isRequired}
			isInvalid={isInvalid}
			onBlur={adaptEventHandler(onBlur)}
			onFocus={adaptEventHandler(onFocus)}
			aria-invalid={ariaInvalid}
			aria-labelledby={ariaLabelledby}
			name={name}
		/>
	);
};
